import { useEffect, useState } from 'react';
import { withStyles, CardTabs } from 'vopay';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Title, Button, UtilButton } from 'UI';
import styles from './ClientAccountPage.module.scss';
import CustomerOverview from '@/components/Customer/Overview/CustomerOverview';
import BalancesCard from './BalancesCard';
import TransactionLimitCard from './TransactionLimitCard';
import DetailsTab from '@/components/Customer/Tabs/Details/DetailsTab';
import SettingsTab from '@/components/Customer/Tabs/Settings/SettingsTab';
import PaymentMethods from '@/components/Customer/Tabs/PaymentMethods/PaymentMethodsTab';
import ActivityTab from '@/components/Customer/Tabs/Activity/ActivityTab';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { usePermissions } from '@/providers/Permissions.provider';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';

const cn = withStyles(styles);

const ClientAccountPage = () => {
  const location = useLocation();
  const history = useHistory();
  const { accountID } = useParams<{ accountID: string }>();
  const { clientAccount, fetchClientAccount } = useClientAccounts();
  const [tab, setTab] = useState('details');
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount' });
  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Customers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const {
    response: { Country },
  } = useClientData();

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const newTab = search.get('cardTab');
    newTab && setTab(newTab);
  }, [location.search]);

  useEffect(() => {
    fetchClientAccount(accountID);
  }, []);

  if (!clientAccount) return null;

  async function handleUpdate() {
    return fetchClientAccount(accountID);
  }

  const accountName = clientAccount.ClientName;

  return (
    <div className={cn('clientAccountPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.replace('/client-accounts');
          }
        }}
      >
        {history.length > 1 ? tGlobal('Buttons.back') : t('clientAccounts', { defaultValue: 'Client Accounts' })}
      </UtilButton>

      <header className={cn('pageHeader')}>
        <Title>{t('pageTitle', { defaultValue: 'Client Account Details' })}</Title>

        <div className={cn('pageHeaderLinks')}>
          {permissions.CreateClientAccountTransfers && (
            <Button
              onClick={() => {
                history.push('/client-account-transfer-funds');
              }}
              disabled={!clientAccount.IsActive}
            >
              {t('transferFunds', { defaultValue: 'Transfer Funds' })}
            </Button>
          )}

          {permissions.CreatePayments && (
            <Button
              onClick={() => {
                history.push('/single-payment');
              }}
              disabled={!clientAccount.IsActive}
            >
              {tCustomer('payment', {
                defaultValue: 'Payment',
              })}
            </Button>
          )}

          {permissions.CreateCollections && (
            <Button
              onClick={() => {
                history.push('/single-collection');
              }}
              disabled={!clientAccount.IsActive}
            >
              {tCustomer('collection', {
                defaultValue: 'Collection',
              })}
            </Button>
          )}
        </div>
      </header>

      <div className={cn('overview')}>
        <CustomerOverview
          accountID={clientAccount.ClientAccountID}
          active={clientAccount.IsActive}
          accountName={accountName}
          lang={clientAccount.Language}
          isBusiness={clientAccount.IsBusiness}
        />

        {clientAccount.Balances && <BalancesCard balances={clientAccount.Balances} country={Country} />}

        <TransactionLimitCard
          currencies={clientAccount.Balances?.map(({ Currency }) => Currency) || ['CAD']}
          limit={clientAccount.TransactionLimit}
          country={Country}
        />
      </div>

      <CardTabs.Root value={tab} onValueChange={setTab}>
        <CardTabs.List
          aria-label={tCustomer('manageYourAccount', {
            defaultValue: 'Manage your account',
          })}
        >
          <CardTabs.Trigger value="details">
            {tCustomer('details', {
              defaultValue: 'Details',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="activity">
            {tCustomer('activity', {
              defaultValue: 'Activity',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="paymentMethods">
            {tCustomer('paymentMethods', {
              defaultValue: 'Payment Methods',
            })}
          </CardTabs.Trigger>
          {/* <CardTabs.Trigger value="settings">Settings</CardTabs.Trigger> */}
        </CardTabs.List>

        <CardTabs.Content value="details">
          <DetailsTab
            account={{
              ...clientAccount,
              AccountID: clientAccount.ClientAccountID,
              AccountName: clientAccount.ClientName,
            }}
            onUpdate={handleUpdate}
          />
        </CardTabs.Content>

        <CardTabs.Content value="activity">
          <ActivityTab accountID={clientAccount.ClientAccountID} active={clientAccount.IsActive} />
        </CardTabs.Content>

        <CardTabs.Content value="paymentMethods" className={cn('tabContent', 'paymentMethodsTab')}>
          <PaymentMethods
            accountID={clientAccount.ClientAccountID}
            accountName={accountName}
            email={clientAccount.EmailAddress}
            active={clientAccount.IsActive}
          />
        </CardTabs.Content>

        <CardTabs.Content value="settings" className={cn('tabContent')}>
          <SettingsTab lang={clientAccount.Language} receiveOnly={clientAccount.ClientType === 'receive-only'} />
        </CardTabs.Content>
      </CardTabs.Root>
    </div>
  );
};

export default ClientAccountPage;
